/**
 * Multi-timeframe confluence badge.
 *
 * The spec asks for a colour that shows agreement at a glance: deep green
 * when every timeframe is long, deep red when every one is short, amber when
 * they disagree. The per-timeframe row underneath shows *why*, including the
 * role each timeframe plays (trigger / filter / context), because "1d says
 * short" only means something once you know the daily is the filter.
 */

import type { MtfConsensus } from '@/api/types'

const ROLE_LABELS: Record<string, string> = {
  trigger: 'kích hoạt',
  filter: 'lọc',
  context: 'bối cảnh',
}

export function ConfluenceBadge({ consensus }: { consensus: MtfConsensus | null }) {
  if (!consensus) {
    return (
      <div className="rounded border border-border bg-panel-alt p-2 text-2xs text-ink-dim">
        Chưa có dữ liệu đồng thuận đa khung
      </div>
    )
  }

  const { tone, label } = describe(consensus)
  const cells = [...consensus.per_tf].sort(
    (a, b) => roleOrder(a.role) - roleOrder(b.role),
  )

  return (
    <div className="rounded border border-border bg-panel-alt p-2">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-2xs uppercase tracking-wider text-ink-dim">Đồng thuận đa khung</span>
        <span className={`rounded px-1.5 py-0.5 text-2xs font-medium ${tone}`}>{label}</span>
      </div>

      <ul className="space-y-1">
        {cells.map((cell) => (
          <li key={cell.timeframe} className="flex items-center gap-2 text-2xs">
            <span className="w-8 font-mono text-ink">{cell.timeframe}</span>
            <span className="w-16 text-ink-dim">{ROLE_LABELS[cell.role] ?? cell.role}</span>
            <span
              className={
                cell.side === 'long' ? 'w-10 text-up' : cell.side === 'short' ? 'w-10 text-down' : 'w-10 text-ink-dim'
              }
            >
              {cell.side === 'long' ? 'mua' : cell.side === 'short' ? 'bán' : '—'}
            </span>
            <ScoreBar score={cell.score} />
            <span className="w-10 text-right font-mono text-ink-dim">
              {cell.score >= 0 ? '+' : ''}{cell.score.toFixed(0)}
            </span>
            {!cell.quorum && (
              <span title="Chưa đủ số nhóm chỉ báo đồng thuận" className="text-warn">!</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function ScoreBar({ score }: { score: number }) {
  const magnitude = Math.min(100, Math.abs(score))
  const positive = score >= 0
  return (
    <span className="relative h-1.5 flex-1 overflow-hidden rounded bg-panel">
      <span
        className={`absolute top-0 h-full ${positive ? 'bg-up' : 'bg-down'}`}
        style={{
          width: `${magnitude / 2}%`,
          left: positive ? '50%' : `${50 - magnitude / 2}%`,
        }}
      />
      <span className="absolute left-1/2 top-0 h-full w-px bg-border" />
    </span>
  )
}

function describe(consensus: MtfConsensus): { tone: string; label: string } {
  if (consensus.conflict) return { tone: 'bg-warn/20 text-warn', label: 'Mâu thuẫn' }
  if (consensus.agree_count >= 3) {
    return consensus.net_score >= 0
      ? { tone: 'bg-up/25 text-up', label: '3 khung cùng mua' }
      : { tone: 'bg-down/25 text-down', label: '3 khung cùng bán' }
  }
  if (consensus.agree_count === 2) {
    return consensus.net_score >= 0
      ? { tone: 'bg-up/15 text-up', label: '2 khung cùng mua' }
      : { tone: 'bg-down/15 text-down', label: '2 khung cùng bán' }
  }
  return { tone: 'bg-panel-hover text-ink-dim', label: 'Chưa rõ' }
}

function roleOrder(role: string): number {
  return { trigger: 0, filter: 1, context: 2 }[role] ?? 3
}
